import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Button, message, Modal, Radio, Table, Typography } from 'antd';
import { Form as FormAntd } from 'antd';
import { ValidateForm } from '../../../../../../../../../../../shared/utils';
import { GenericDataFormContext, QuotationInfoContext } from '../../../../../../../shared';
import { StepFourFormContext } from '../../../../../StepFour/contexts/StepFourForm';
import { StepFiveFormContext } from '../../contexts/StepFiveForm';
import { ColSize, ContentSection, Form, InputForm, RowSize, SelectForm } from '../../../../../../../../../../../shared/components';
import { TableTransferRightsItem, TransferRightsFormData, TransferRightsFormProps } from '../../../../../../../../../../../shared/interfaces/services/transfer-rights-service';
import { StepThreeFormContext } from '../../../../../StepThree/contexts/StepThreeForm';
import { StepTwoFormContext } from '../../../../../StepTwo/contexts/StepTwoForm';
import { PERSON_TYPE } from '../../../../../../../../../../../shared/interfaces/enums';
import { ActionButton } from '@hdi-seguros/web-components.action-button';
import { DeleteOutlined } from '@ant-design/icons';

export const TransferRightsForm: FC<TransferRightsFormProps> = ({ commerceComponent }) => {
  const validateForm = new ValidateForm();
  const { allCoveragesPerPlan } = useContext(GenericDataFormContext);
  const { quotationInfo } = useContext(QuotationInfoContext);
  const { quotationResponse } = useContext(StepFourFormContext);
  const { transferRightsList, setTransferRightsList } = useContext(StepFiveFormContext);
  const { stepThreeForm } = useContext(StepThreeFormContext);
  const { rubricSelected } = useContext(StepTwoFormContext);

  const [transferRightsForm] = FormAntd.useForm();
  const [showTransferModal, setShowTransferModal] = useState<boolean>(false);
  const [transferRightsTableList, setTransferRightsTableList] = useState<TableTransferRightsItem[]>([]);
  const [selectedCoverage, setSelectedCoverage] = useState<string>('');
  const [personType, setPersonType] = useState<'F' | 'J'>(PERSON_TYPE.FISICA.codigo as 'F' | 'J');
  const [availableCoverages, setAvailableCoverages] = useState<any[]>([]);
  const [isLoadingValidation, setIsLoadingValidation] = useState<boolean>(false);

  const getAvailableCoverages = useCallback(async () => {
    if (!commerceComponent?.coberturas || commerceComponent.coberturas.length === 0) return;

    const validCoverages: any[] = [];
    
    for (const coverage of commerceComponent.coberturas) {
      try {
        const planSelected = stepThreeForm?.getFieldValue(`plan-tab-${commerceComponent.nroDeComponente}`);
        
        const matchingCoveragesByTab = allCoveragesPerPlan?.find(
          (el: any) => String(el.plan as string) === String(planSelected)
        );

        let coverageDescription = coverage.codigoCobertura;
        let permiteTransDerechos = false;
        
        if (matchingCoveragesByTab) {
          const planCoverage = matchingCoveragesByTab.coverages?.find(
            (planCov: any) => 
              planCov.codigo === coverage.codigoCobertura && 
              String(planCov.codigoRiesgo) === String(rubricSelected?.codigoDeRiesgo)
          );
          
          if (planCoverage) {
            coverageDescription = planCoverage.descripcionLarga || planCoverage.descripcionCorta || coverage.codigoCobertura;
            permiteTransDerechos = planCoverage.permiteTransDerechos === "S";
          }
        }

        if(permiteTransDerechos){
          validCoverages.push({
            value: `${coverage.codigoCobertura}-${coverage.codigoRiesgo}`,
            label: coverageDescription,
            codigoCobertura: Number(coverage.codigoCobertura),
            codigoRiesgo: coverage.codigoRiesgo,
          });
        }
      } catch (error) {
        console.error('Error validating coverage:', error);
      }
    }

    setAvailableCoverages(validCoverages);
  }, [commerceComponent?.coberturas, quotationResponse, quotationInfo, allCoveragesPerPlan, stepThreeForm, rubricSelected]);

  const onDeleteTransferRights = (key: string) => {
    const filteredTransferRights = transferRightsTableList.filter((el: TableTransferRightsItem) => el.key !== key);
    setTransferRightsTableList(filteredTransferRights);
    
    const keyIndex = Number(key) - 1;
    const updatedContextList = transferRightsList.filter((_: TransferRightsFormData, index: number) => index !== keyIndex);
    setTransferRightsList(updatedContextList);
  };

  const addNewTransferRightsOnTable = useCallback(() => {
    const formFields = transferRightsForm.getFieldsValue();
    const selectedCov = availableCoverages.find((cov: any) => cov.value === selectedCoverage);

    if (!selectedCov) return;

    let nombreCompleto = '';
    let documento = '';

    if (personType === PERSON_TYPE.FISICA.codigo) {
      nombreCompleto = `${formFields.nombre} ${formFields.apellido}`;
      documento = `${formFields.dni}`;
    } else {
      nombreCompleto = formFields.denominacion;
      documento = `${formFields.cuit}`;
    }

    const personTypeInfo = personType === PERSON_TYPE.FISICA.codigo ? PERSON_TYPE.FISICA : PERSON_TYPE.JURIDICA;

    const newTransferRightsTable: TableTransferRightsItem = {
      key: String(transferRightsTableList.length + 1),
      cobertura: selectedCov.label,
      tipoPersona: personTypeInfo.descripcion,
      nombreCompleto,
      documento,
      codigoCobertura: selectedCov.codigoCobertura,
      codigoRiesgo: selectedCov.codigoRiesgo,
    };

    const newTransferRightsContext: TransferRightsFormData = {
      cobertura: String(selectedCov.codigoCobertura),
      riesgo: selectedCov.codigoRiesgo,
      tipoPersona: personType,
      ...(personType === PERSON_TYPE.FISICA.codigo 
        ? { 
            nombre: formFields.nombre, 
            apellido: formFields.apellido, 
            dni: formFields.dni 
          }
        : { 
            denominacion: formFields.denominacion, 
            cuit: formFields.cuit 
          }
      ),
    };

    setTransferRightsTableList((prevValues: TableTransferRightsItem[]) => [...prevValues, newTransferRightsTable]);
    setTransferRightsList((prevValues: TransferRightsFormData[]) => [...prevValues, newTransferRightsContext]);
    
    setShowTransferModal(false);
    transferRightsForm.resetFields();
    setSelectedCoverage('');
    setPersonType(PERSON_TYPE.FISICA.codigo as 'F' | 'J');
  }, [transferRightsForm, transferRightsTableList, selectedCoverage, personType, availableCoverages, setTransferRightsList]);

  const onAddNewTransferRights = useCallback(async () => {
    try {
      if (!selectedCoverage) {
        message.error('Debe seleccionar una cobertura.');
        return;
      }

      await transferRightsForm.validateFields();
      addNewTransferRightsOnTable();
    } catch (error) {
      
    }
  }, [selectedCoverage, transferRightsForm, addNewTransferRightsOnTable]);

  const onCancelTransferModal = () => {
    transferRightsForm.resetFields();
    setShowTransferModal(false);
    setSelectedCoverage('');
    setPersonType(PERSON_TYPE.FISICA.codigo as 'F' | 'J');
  };

  const TABLE_COLUMNS_HEADER = [
    {
      title: 'Cobertura',
      dataIndex: 'cobertura',
      width: '30%',
    },
    {
      title: 'Tipo de Persona',
      dataIndex: 'tipoPersona',
      width: '20%',
    },
    {
      title: 'Nombre Completo',
      dataIndex: 'nombreCompleto',
      width: '30%',
    },
    {
      title: 'Documento',
      dataIndex: 'documento',
      width: '15%',
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: '5%',
      render: (_: any, record: TableTransferRightsItem) => (
        <ActionButton
            label=''
            icon={<DeleteOutlined />}
            color='danger'
            type='secondary'
            onClick={() => onDeleteTransferRights(record.key)}
          />
      ),
    },
  ];

  useEffect(() => {
    getAvailableCoverages();
  }, [getAvailableCoverages]);

  useEffect(() => {
    const syncedTableList = transferRightsList.map((item, index) => {
      const personTypeInfo = item.tipoPersona === PERSON_TYPE.FISICA.codigo ? PERSON_TYPE.FISICA : PERSON_TYPE.JURIDICA;
      const nombreCompleto = item.nombre && item.apellido ? `${item.nombre} ${item.apellido}` : item.denominacion || '';
      const documento = item.dni || item.cuit || '';
      
      // Buscar la descripción de la cobertura desde availableCoverages
      const coberturaData = availableCoverages.find(
        (cov) => Number(cov.codigoCobertura) === Number(item.cobertura)
      );
      const coberturaLabel = coberturaData?.label || item.cobertura || '';
      
      return {
        key: String(index + 1),
        cobertura: coberturaLabel,
        tipoPersona: personTypeInfo.descripcion,
        nombreCompleto,
        documento,
        codigoCobertura: Number(item.cobertura),
        codigoRiesgo: item.riesgo,
      };
    });
    setTransferRightsTableList(syncedTableList);
  }, [transferRightsList, availableCoverages]);

  if (availableCoverages.length === 0) {
    return null; 
  }

  return (
    <ContentSection level='h3' title='Transferencia de derechos' hasCard={false}>
      <RowSize>
        <ColSize size={100}>
          <Button
            type="primary"
            onClick={() => setShowTransferModal(true)}
            style={{ marginBottom: 16 }}
          >
            Agregar Transferencia de Derechos
          </Button>
        </ColSize>
      </RowSize>

      {transferRightsTableList.length > 0 && (
        <Table
          bordered
          className='w-100'
          columns={TABLE_COLUMNS_HEADER}
          dataSource={transferRightsTableList}
          pagination={false}
        />
      )}

      <Modal
        title="Agregar Transferencia de Derechos"
        open={showTransferModal}
        onOk={onAddNewTransferRights}
        onCancel={onCancelTransferModal}
        width={600}
        okText="Agregar"
        cancelText="Cancelar"
      >
        <Form
          formProps={{
            form: transferRightsForm,
            layout: 'vertical',
          }}
        >
          <RowSize>
            <ColSize size={100}>
              <SelectForm
                formProps={{
                  label: 'Cobertura',
                  name: 'cobertura',
                  rules: validateForm.generateRules({
                    label: 'cobertura',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                  placeholder: 'Seleccione una cobertura',
                  options: availableCoverages,
                  onChange: (value: string) => setSelectedCoverage(value),
                }}
              />
            </ColSize>
          </RowSize>

          <RowSize>
            <ColSize size={100}>
              <Typography.Text strong>Tipo de Persona:</Typography.Text>
              <Radio.Group
                value={personType}
                onChange={(e: any) => {
                  const value = e.target.value as 'F' | 'J';
                  setPersonType(value);
                }}
                style={{ marginTop: 8, marginBottom: 16 }}
              >
                <Radio value={PERSON_TYPE.FISICA.codigo}>{PERSON_TYPE.FISICA.descripcion}</Radio>
                <Radio value={PERSON_TYPE.JURIDICA.codigo}>{PERSON_TYPE.JURIDICA.descripcion}</Radio>
              </Radio.Group>
            </ColSize>
          </RowSize>

          {personType === PERSON_TYPE.FISICA.codigo ? (
            <>
              <RowSize>
                <ColSize size={50}>
                  <InputForm
                    formProps={{
                      label: 'Nombre',
                      name: 'nombre',
                      rules: validateForm.generateRules({
                        label: 'nombre',
                        rules: ['required'],
                      }),
                    }}
                    inputProps={{ placeholder: 'Ingrese el nombre' }}
                  />
                </ColSize>
                <ColSize size={50}>
                  <InputForm
                    formProps={{
                      label: 'Apellido',
                      name: 'apellido',
                      rules: validateForm.generateRules({
                        label: 'apellido',
                        rules: ['required'],
                      }),
                    }}
                    inputProps={{ placeholder: 'Ingrese el apellido' }}
                  />
                </ColSize>
              </RowSize>
              <RowSize>
                <ColSize size={100}>
                  <InputForm
                    formProps={{
                      label: 'DNI',
                      name: 'dni',
                      rules: validateForm.generateRules({
                        label: 'DNI',
                        rules: ['required', 'number'],
                      }),
                    }}
                    inputProps={{ placeholder: 'Ingrese DNI' }}
                  />
                </ColSize>
              </RowSize>
            </>
          ) : (
            <>
              <RowSize>
                <ColSize size={100}>
                  <InputForm
                    formProps={{
                      label: 'Denominación',
                      name: 'denominacion',
                      rules: validateForm.generateRules({
                        label: 'denominación',
                        rules: ['required'],
                      }),
                    }}
                    inputProps={{ placeholder: 'Ingrese la denominación' }}
                  />
                </ColSize>
              </RowSize>
              <RowSize>
                <ColSize size={100}>
                  <InputForm
                    formProps={{
                      label: 'CUIT',
                      name: 'cuit',
                      rules: validateForm.generateRules({
                        label: 'CUIT',
                        rules: ['required', 'cuil'],
                      }),
                    }}
                    inputProps={{ placeholder: 'Ingrese CUIT' }}
                  />
                </ColSize>
              </RowSize>
            </>
          )}
        </Form>
      </Modal>
    </ContentSection>
  );
};
